import React, { useState, useEffect } from 'react';
import type { AIRule } from '../types';
import { rulesAPI } from '../api';
import { BookOpen, Plus, Trash, Sparkles } from 'lucide-react';
import { useAlert } from '../contexts/AlertContext';

export const AIRules: React.FC = () => {
  const { showAlert, showConfirm } = useAlert();
  const [rules, setRules] = useState<AIRule[]>([]);
  const [loading, setLoading] = useState(true);

  // Form states
  const [term, setTerm] = useState('');
  const [mappedArea, setMappedArea] = useState('Saúde');
  const [saving, setSaving] = useState(false);

  const loadRules = async () => {
    setLoading(true);
    try {
      const data = await rulesAPI.list();
      setRules(data);
    } catch (e) {
      showAlert('error', 'Erro', 'Erro ao carregar dicionários.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadRules();
  }, []);

  const handleAddRule = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!term) return;
    setSaving(true);
    try {
      const newRule = await rulesAPI.create(term, mappedArea);
      setRules([newRule, ...rules]);
      setTerm('');
      showAlert('success', 'Sucesso', 'Regra de IA criada com sucesso!');
    } catch (err) {
      showAlert('error', 'Erro', 'Erro ao adicionar regra.');
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteRule = async (id: string) => {
    showConfirm('Excluir Regra', 'Deseja excluir esta regra de classificação?', async () => {
      try {
        await rulesAPI.delete(id);
        setRules(rules.filter(r => r.id !== id));
      } catch (e) {
        showAlert('error', 'Erro', 'Erro ao excluir regra.');
      }
    }, true);
  };

  const areasList = [
    'Saúde', 'Educação', 'Infraestrutura', 'Cultura', 'Assistência Social',
    'Segurança Pública', 'Esporte', 'Agricultura', 'Meio Ambiente', 'Habitação',
    'Transporte', 'Turismo', 'Desenvolvimento Econômico', 'Direitos Humanos'
  ];

  return (
    <div className="space-y-6 text-left">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold tracking-tight font-sans">Módulo de Treinamento da IA</h1>
        <p className="text-sm text-slate-500 dark:text-slate-400">
          Cadastre regras de correspondência que ensinam a IA a classificar demandas e mapear órgãos automaticamente.
        </p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 items-start">
        {/* Formulário de Criação (1/3) */}
        <div className="glass-panel p-6 rounded-3xl space-y-4">
          <h2 className="font-semibold text-sm border-b pb-3 border-slate-100 dark:border-slate-800 flex items-center gap-1.5">
            <Sparkles className="w-4 h-4 text-blue-500" />
            Nova Regra de Mapeamento
          </h2>

          <form onSubmit={handleAddRule} className="space-y-4 text-xs">
            <div>
              <label className="block text-slate-400 mb-1 font-medium">Palavra-chave ou Termo do Ofício</label>
              <input
                type="text"
                required
                placeholder="Ex: SCFV, UBS, Estrada Vicinal..."
                className="w-full bg-slate-50 dark:bg-slate-950 border border-slate-200 dark:border-slate-900 rounded-xl p-2.5 focus:outline-none focus:border-blue-500"
                value={term}
                onChange={(e) => setTerm(e.target.value)}
              />
              <span className="text-[10px] text-slate-400 mt-1 block">
                Se este termo for detectado no ofício, a IA forçará a área temática selecionada.
              </span>
            </div>

            <div>
              <label className="block text-slate-400 mb-1 font-medium">Área Temática Associada</label>
              <select
                className="w-full bg-slate-50 dark:bg-slate-950 border border-slate-200 dark:border-slate-900 rounded-xl p-2.5 focus:outline-none focus:border-blue-500"
                value={mappedArea}
                onChange={(e) => setMappedArea(e.target.value)}
              >
                {areasList.map(area => (
                  <option key={area} value={area}>{area}</option>
                ))}
              </select>
            </div>

            <button
              type="submit"
              disabled={saving}
              className="w-full py-2.5 bg-blue-600 hover:bg-blue-500 text-white font-bold rounded-xl transition-all shadow-md shadow-blue-500/10 flex items-center justify-center gap-1"
            >
              <Plus className="w-4 h-4" />
              {saving ? 'Gravando...' : 'Cadastrar Regra'}
            </button>
          </form>
        </div>

        {/* Lista de Regras Existentes (2/3) */}
        <div className="glass-panel p-6 rounded-3xl lg:col-span-2 space-y-4">
          <h2 className="font-semibold text-sm border-b pb-3 border-slate-100 dark:border-slate-800 flex items-center gap-1.5">
            <BookOpen className="w-4 h-4 text-slate-400" />
            Regras de Treinamento Ativas
          </h2>

          {loading ? (
            <div className="py-12 text-center text-xs text-slate-500">
              Carregando regras da IA...
            </div>
          ) : rules.length === 0 ? (
            <div className="py-12 text-center text-xs text-slate-500">
              Nenhuma regra cadastrada ainda.
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-left border-collapse text-xs">
                <thead>
                  <tr className="bg-slate-100 dark:bg-slate-900/50 border-b border-slate-200 dark:border-slate-800 font-semibold text-slate-400 uppercase tracking-wider">
                    <th className="py-3 px-4">Palavra-chave / Termo</th>
                    <th className="py-3 px-3">Área Mapeada</th>
                    <th className="py-3 px-3">Criado em</th>
                    <th className="py-3 px-4 text-right">Ação</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100 dark:divide-slate-800/60">
                  {rules.map(rule => (
                    <tr key={rule.id} className="hover:bg-slate-500/5 transition-all">
                      <td className="py-3.5 px-4 font-bold text-slate-800 dark:text-slate-200">{rule.term}</td>
                      <td className="py-3.5 px-3">
                        <span className="px-2.5 py-0.5 bg-blue-100 dark:bg-blue-950/40 text-blue-800 dark:text-blue-400 font-semibold rounded-full border border-blue-200/20">
                          {rule.mappedArea}
                        </span>
                      </td>
                      <td className="py-3.5 px-3 text-slate-400">
                        {new Date(rule.createdAt).toLocaleDateString('pt-BR')}
                      </td>
                      <td className="py-3.5 px-4 text-right">
                        <button
                          onClick={() => handleDeleteRule(rule.id)}
                          className="p-1.5 text-red-500 hover:bg-red-500/10 hover:text-red-600 rounded-lg transition-all"
                          title="Remover Regra"
                        >
                          <Trash className="w-4 h-4" />
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};
